// Phase 2 of PDF export: capture the on-screen recharts SVGs as raster images so
// they can be embedded in the PDF (react-pdf can't render live SVG/DOM). Browser-
// native only — no html2canvas/file-saver. Each chart is marked in the DOM with
// `data-ai-chart`; we walk them in document order, which matches the chart order
// `collectExportContent` sees. If capture fails/times out for any chart we skip it
// (the collector falls back to that chart's data table).
//
// HARD CAPS are essential: a stray/huge `getBoundingClientRect()` would otherwise
// build a giant canvas whose synchronous `toDataURL` freezes the tab. We clamp the
// output size, rasterise at 1× as JPEG (far cheaper for react-pdf to embed than a
// large PNG), and time out each capture.

export interface CapturedImage {
    dataUrl: string;
    width: number;
    height: number;
}

const MAX_W = 1000;          // px — cap the LAYOUT size (prevents runaway toDataURL)
const MAX_H = 700;
const SUPERSAMPLE = 2;       // rasterise at 2× for crisp lines/text in the PDF
const CAPTURE_TIMEOUT_MS = 3000;

const svgToImage = (svg: SVGSVGElement): Promise<CapturedImage> =>
    new Promise((resolve, reject) => {
        const rect = svg.getBoundingClientRect();
        let width = Math.round(rect.width) || 800;
        let height = Math.round(rect.height) || 300;
        // Fit within the caps, preserving aspect ratio.
        const scale = Math.min(1, MAX_W / width, MAX_H / height);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('width', String(width));
        clone.setAttribute('height', String(height));

        const xml = new XMLSerializer().serializeToString(clone);
        const svg64 = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(xml)))}`;

        let done = false;
        const timer = window.setTimeout(() => {
            if (done) return;
            done = true;
            reject(new Error('chart capture timed out'));
        }, CAPTURE_TIMEOUT_MS);

        const img = new Image();
        img.onload = () => {
            if (done) return;
            done = true;
            window.clearTimeout(timer);
            try {
                // Supersample: rasterise at SUPERSAMPLE× the layout size for crisp
                // lines/text in the PDF, but return width/height = the LAYOUT size so
                // react-pdf lays it out normally and downscales the hi-res pixels. The
                // canvas stays bounded (base is capped to MAX_W×MAX_H), so no freeze.
                const canvas = document.createElement('canvas');
                canvas.width = width * SUPERSAMPLE;
                canvas.height = height * SUPERSAMPLE;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('no 2d context'));
                    return;
                }
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                // JPEG at high quality: crisp at 2× supersampling, far smaller than PNG.
                resolve({dataUrl: canvas.toDataURL('image/jpeg', 0.92), width, height});
            } catch (e) {
                reject(e as Error);
            }
        };
        img.onerror = () => {
            if (done) return;
            done = true;
            window.clearTimeout(timer);
            reject(new Error('svg image load failed'));
        };
        img.src = svg64;
    });

/**
 * Capture every chart currently in the DOM, keyed by its document-order index
 * (which matches `allCharts(messages)` in the export collector). A chart that
 * fails/times out is omitted — the collector then renders it as a data table, so the
 * PDF is never blank (or hung) for that chart.
 */
export const captureCharts = async (): Promise<Map<number, CapturedImage>> => {
    const svgs = Array.from(document.querySelectorAll<SVGSVGElement>('[data-ai-chart] svg'));
    const out = new Map<number, CapturedImage>();
    await Promise.all(
        svgs.map(async (svg, i) => {
            try {
                out.set(i, await svgToImage(svg));
            } catch {
                // skip — collector falls back to the chart's data table
            }
        }),
    );
    return out;
};

/**
 * Capture the off-screen export rig (`ChartExportRig`) — one image per chart PER
 * METRIC — so the PDF shows all four metric views. Keyed by `"{chartIdx}:{metric}"`.
 * A cell that fails/times out is simply omitted (that metric falls back). Reuses the
 * same hard-capped `svgToImage` (JPEG + size caps + timeout — the anti-freeze logic).
 */
export const captureExportCharts = async (): Promise<Map<string, CapturedImage>> => {
    const cells = Array.from(document.querySelectorAll<HTMLElement>('[data-ai-export-chart]'));
    const out = new Map<string, CapturedImage>();
    await Promise.all(
        cells.map(async (cell) => {
            const chartIdx = cell.getAttribute('data-chart-idx');
            const metric = cell.getAttribute('data-metric');
            const svg = cell.querySelector('svg');
            if (chartIdx === null || metric === null || !svg) return;
            try {
                out.set(`${chartIdx}:${metric}`, await svgToImage(svg));
            } catch {
                // skip — that metric image is omitted
            }
        }),
    );
    return out;
};
